/**
 * AttributeTable - 속성 테이블 컴포넌트
 * 새 창(팝업 윈도우)으로 열기 - QGIS 스타일
 */

import { eventBus, Events } from '../../utils/EventBus.js';
import { escapeHtml } from '../../utils/escapeHtml.js';
import { layerManager } from '../../core/LayerManager.js';
import { mapManager } from '../../core/MapManager.js';
import Select from 'ol/interaction/Select';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';

// 하이라이트 스타일
const HIGHLIGHT_STYLE = new Style({
  fill: new Fill({ color: 'rgba(255, 193, 7, 0.4)' }),
  stroke: new Stroke({ color: '#ffc107', width: 3 }),
  image: new CircleStyle({
    radius: 8,
    fill: new Fill({ color: '#ffc107' }),
    stroke: new Stroke({ color: '#fff', width: 2 })
  })
});

export class AttributeTable {
  constructor() {
    this.openWindows = new Map(); // layerId -> window
    this.highlightSelects = new Map(); // layerId -> Select interaction
    this.mobileSheet = null; // 모바일 오버레이 { layerId, el }

    this.bindEvents();
  }

  /**
   * 이벤트 바인딩
   */
  bindEvents() {
    // 레이어 테이블 열기 이벤트
    eventBus.on('layer:openTable', ({ layerId }) => {
      this.open(layerId);
    });

    // 레이어 삭제 시 창 닫기
    eventBus.on(Events.LAYER_REMOVED, ({ layerId }) => {
      this.closeWindow(layerId);
      this.closeMobileSheet(layerId);
    });

    // 피처 변경 시 테이블 갱신
    eventBus.on(Events.FEATURE_CREATED, () => {
      this.refreshAllWindows();
    });

    eventBus.on(Events.FEATURE_DELETED, ({ source } = {}) => {
      // 속성 테이블 자체 삭제는 이미 DOM을 갱신했으므로 재갱신(닫고 다시 열기) 불필요
      if (source === 'attributeTable') return;
      this.refreshAllWindows();
    });

    // 합치기: 여러 행이 한 행이 된다. 되돌리면 다시 여러 행으로 돌아온다
    eventBus.on(Events.FEATURES_MERGED, () => {
      this.refreshAllWindows();
    });
  }

  /**
   * 속성 테이블 열기 (새 창)
   */
  open(layerId) {
    const layerInfo = layerManager.getLayer(layerId);
    if (!layerInfo) return;
    if (!layerInfo.source) {
      alert('이 레이어에는 속성 테이블이 없습니다. (흐름·래스터 레이어)');
      return;
    }

    // 모바일/태블릿(터치 기기): 팝업 창이 차단되므로 전체 화면 오버레이로 표시
    // (태블릿 판별은 기존 태블릿 최적화 미디어쿼리와 동일 기준)
    const useOverlay = window.matchMedia('(max-width: 768px)').matches ||
      window.matchMedia('(pointer: coarse) and (max-width: 1366px)').matches;
    if (useOverlay) {
      this.openMobileSheet(layerId, layerInfo);
      return;
    }

    // 이미 열린 창이 있으면 포커스
    if (this.openWindows.has(layerId)) {
      const existingWin = this.openWindows.get(layerId);
      if (existingWin && !existingWin.closed) {
        existingWin.focus();
        return;
      }
    }

    // 피처 및 컬럼 추출
    const features = layerInfo.source.getFeatures();
    const columns = this.extractColumns(features);

    // 새 창 열기
    const windowWidth = 900;
    const windowHeight = 500;
    const left = (screen.width - windowWidth) / 2;
    const top = (screen.height - windowHeight) / 2;

    const win = window.open(
      '',
      `attribute_table_${layerId}`,
      `width=${windowWidth},height=${windowHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!win) {
      // 팝업이 차단된 환경에서는 전체 화면 오버레이로 대체
      this.openMobileSheet(layerId, layerInfo);
      return;
    }

    this.openWindows.set(layerId, win);

    // HTML 작성
    const html = this.generateHTML(layerInfo, features, columns, layerId);
    win.document.write(html);
    win.document.close();

    // 창 닫힘 감지
    const checkClosed = setInterval(() => {
      if (win.closed) {
        clearInterval(checkClosed);
        this.removeHighlight(layerId);
        this.openWindows.delete(layerId);
      }
    }, 500);

    // 하이라이트 인터랙션 설정
    this.setupHighlight(layerId, layerInfo);

    // 창 내부 이벤트 바인딩
    this.bindWindowEvents(win, layerId, features, columns);
  }

  /**
   * 속성 테이블 열기 (모바일: 전체 화면 오버레이)
   * 보기/정렬/선택/지도 이동 + 편집 모드(셀 수정)/삭제 지원
   */
  openMobileSheet(layerId, layerInfo) {
    this.closeMobileSheet();

    const features = layerInfo.source.getFeatures();
    const columns = this.extractColumns(features);

    const headerCells = columns.map(col =>
      `<th data-column="${col}">${col}<span class="attr-sort-icon"></span></th>`
    ).join('');

    const bodyRows = features.map((feature, index) => {
      const cells = columns.map(col => {
        const value = feature.get(col);
        const displayValue = value !== undefined && value !== null ? value : '';
        return `<td>${displayValue}</td>`;
      }).join('');
      return `<tr data-feature-id="${feature.ol_uid}"><td class="row-num">${index + 1}</td>${cells}</tr>`;
    }).join('');

    const HINT_DEFAULT = '행 탭: 선택/해제 · 머리글 탭: 정렬 · 선택 이동: 지도에서 보기';
    const HINT_EDIT = '✏️ 편집 모드: 수정할 셀을 탭하세요. 완료하려면 편집을 다시 탭하세요.';

    const overlay = document.createElement('div');
    overlay.className = 'attr-table-mobile';
    overlay.innerHTML = `
      <div class="attr-mobile-header">
        <div class="attr-mobile-title">${escapeHtml(layerInfo.name)}
          <span class="attr-mobile-count">${features.length}개</span>
          <span class="attr-mobile-selcount"></span>
        </div>
        <button class="attr-mobile-close" id="attr-mobile-close" aria-label="닫기">&times;</button>
      </div>
      <div class="attr-mobile-toolbar">
        <div class="attr-mobile-group">
          <button class="btn btn-sm" id="attr-mobile-edit">✏️ 편집</button>
          <button class="btn btn-sm btn-danger" id="attr-mobile-delete" disabled>선택 삭제</button>
          <button class="btn btn-sm btn-primary" id="attr-mobile-zoom" disabled>선택 이동</button>
        </div>
        <div class="attr-mobile-group">
          <span class="attr-mobile-group-label">열 추가</span>
          <input type="text" id="attr-mobile-newcol" placeholder="이름" style="flex:0 1 78px;min-width:0;padding:6px 8px;border-radius:4px;border:1px solid var(--border-color,#ccc);font-size:13px;">
          <button class="btn btn-sm" id="attr-mobile-addcol" title="새 열 추가">추가</button>
        </div>
        <div class="attr-mobile-group">
          <span class="attr-mobile-group-label">열 삭제</span>
          <select id="attr-mobile-delcol" title="삭제할 열 선택" style="flex:0 1 78px;min-width:0;padding:6px 8px;border-radius:4px;border:1px solid var(--border-color,#ccc);font-size:13px;"></select>
          <button class="btn btn-sm btn-danger" id="attr-mobile-delcol-btn" title="선택한 열 삭제">삭제</button>
        </div>
        <div class="attr-mobile-group">
          <button class="btn btn-sm" id="attr-mobile-csv" title="CSV로 저장">CSV</button>
        </div>
      </div>
      <div class="attr-mobile-hint">${HINT_DEFAULT}</div>
      <div class="attr-mobile-table-wrap">
        <table class="attr-mobile-table">
          <thead><tr><th class="row-num">#</th>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </div>
    `;

    document.body.appendChild(overlay);
    this.mobileSheet = { layerId, el: overlay };

    // 지도 하이라이트 인터랙션
    this.setupHighlight(layerId, layerInfo);

    const featureMap = new Map();
    features.forEach(f => featureMap.set(String(f.ol_uid), f));

    const selected = new Set();
    const tbody = overlay.querySelector('tbody');
    const zoomBtn = overlay.querySelector('#attr-mobile-zoom');
    const editBtn = overlay.querySelector('#attr-mobile-edit');
    const deleteBtn = overlay.querySelector('#attr-mobile-delete');
    const selcountEl = overlay.querySelector('.attr-mobile-selcount');
    const countEl = overlay.querySelector('.attr-mobile-count');
    const hintEl = overlay.querySelector('.attr-mobile-hint');

    const updateHighlight = () => {
      const hs = this.highlightSelects.get(layerId);
      if (!hs) return;
      hs.getFeatures().clear();
      selected.forEach(fid => {
        const f = featureMap.get(fid);
        if (f) hs.getFeatures().push(f);
      });
    };

    const updateSelectionUI = () => {
      selcountEl.textContent = selected.size > 0 ? `· ${selected.size}개 선택` : '';
      zoomBtn.disabled = selected.size === 0;
      deleteBtn.disabled = selected.size === 0;
    };

    // ----- 편집 모드 (셀 수정) -----
    let editMode = false;
    let editingCell = null; // { td, fid, column, originalValue }

    const finishEditing = (save) => {
      if (!editingCell) return;
      const { td, fid, column, originalValue } = editingCell;
      editingCell = null;
      const input = td.querySelector('input');
      const newValue = input ? input.value : null;
      const feature = featureMap.get(fid);

      let displayValue = originalValue !== undefined && originalValue !== null ? originalValue : '';
      if (save && feature && newValue !== null && newValue !== String(displayValue)) {
        // 숫자 변환 시도 (데스크톱 버전과 동일)
        const num = parseFloat(newValue);
        const finalValue = (!isNaN(num) && newValue.trim() !== '') ? num : newValue;
        feature.set(column, finalValue);
        displayValue = newValue;
      }

      td.classList.remove('editing');
      td.innerHTML = '';
      td.textContent = displayValue;
    };

    const startEditing = (td, tr) => {
      finishEditing(true);
      const fid = tr.dataset.featureId;
      const feature = featureMap.get(fid);
      if (!feature) return;

      const cells = Array.from(tr.querySelectorAll('td'));
      const cellIndex = cells.indexOf(td) - 1; // row-num 셀 제외
      if (cellIndex < 0 || cellIndex >= columns.length) return;

      const column = columns[cellIndex];
      const originalValue = feature.get(column);
      editingCell = { td, fid, column, originalValue };

      td.classList.add('editing');
      const input = document.createElement('input');
      input.type = 'text';
      input.value = originalValue !== undefined && originalValue !== null ? originalValue : '';
      td.innerHTML = '';
      td.appendChild(input);
      input.focus();

      input.addEventListener('blur', () => finishEditing(true));
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') finishEditing(true);
        else if (ev.key === 'Escape') finishEditing(false);
        ev.stopPropagation();
      });
    };

    editBtn.addEventListener('click', () => {
      finishEditing(true);
      editMode = !editMode;
      editBtn.classList.toggle('active', editMode);
      hintEl.textContent = editMode ? HINT_EDIT : HINT_DEFAULT;
    });

    // CSV 다운로드
    overlay.querySelector('#attr-mobile-csv').addEventListener('click', () => {
      finishEditing(true);
      this.downloadCsv(layerId);
    });

    // 열 추가
    overlay.querySelector('#attr-mobile-addcol').addEventListener('click', () => {
      finishEditing(true);
      const input = overlay.querySelector('#attr-mobile-newcol');
      const name = (input ? input.value : '').trim();
      if (!name) {
        alert('추가할 열 이름을 입력하세요.');
        return;
      }
      if (columns.includes(name) || name === 'geometry') {
        alert('이미 존재하는 열 이름입니다.');
        return;
      }
      if (featureMap.size === 0) {
        alert('피처가 없어 열을 추가할 수 없습니다.');
        return;
      }
      // 모든 피처에 빈 값 속성 추가 후 시트 재구성 (새 열 반영)
      featureMap.forEach(f => {
        if (f.get(name) === undefined) f.set(name, '');
      });
      this.openMobileSheet(layerId, layerInfo);
    });

    // 열 삭제 - 선택 목록 채우기
    const delColSel = overlay.querySelector('#attr-mobile-delcol');
    if (delColSel) {
      if (columns.length === 0) {
        const opt = document.createElement('option');
        opt.value = '';
        opt.textContent = '삭제할 열 없음';
        delColSel.appendChild(opt);
      } else {
        columns.forEach(col => {
          const opt = document.createElement('option');
          opt.value = col;
          opt.textContent = col;
          delColSel.appendChild(opt);
        });
      }
    }

    // 열 삭제
    overlay.querySelector('#attr-mobile-delcol-btn').addEventListener('click', () => {
      finishEditing(true);
      const name = delColSel ? delColSel.value : '';
      if (!name) {
        alert('삭제할 열을 선택하세요.');
        return;
      }
      if (!confirm(`'${name}' 열을 삭제하시겠습니까? 이 열의 모든 값이 사라집니다.`)) return;
      // 모든 피처에서 속성 제거 후 시트 재구성
      featureMap.forEach(f => f.unset(name));
      this.openMobileSheet(layerId, layerInfo);
    });

    // 행 탭: 일반 모드 → 선택 토글 / 편집 모드 → 셀 수정
    tbody.addEventListener('click', (e) => {
      const tr = e.target.closest('tr');
      if (!tr) return;

      if (editMode) {
        const td = e.target.closest('td');
        if (!td || td.classList.contains('row-num')) return;
        if (editingCell && editingCell.td === td) return;
        startEditing(td, tr);
        return;
      }

      const fid = tr.dataset.featureId;
      if (selected.has(fid)) {
        selected.delete(fid);
        tr.classList.remove('selected');
      } else {
        selected.add(fid);
        tr.classList.add('selected');
      }
      updateSelectionUI();
      updateHighlight();
    });

    // ----- 선택 피처 삭제 -----
    deleteBtn.addEventListener('click', () => {
      if (selected.size === 0) return;
      const count = selected.size;
      const message = count === 1
        ? '선택한 피처를 삭제하시겠습니까?'
        : `선택한 ${count}개의 피처를 삭제하시겠습니까?`;
      if (!confirm(message)) return;

      selected.forEach(fid => {
        const f = featureMap.get(fid);
        if (f) {
          layerInfo.source.removeFeature(f);
          featureMap.delete(fid);
        }
        const row = tbody.querySelector(`tr[data-feature-id="${fid}"]`);
        if (row) row.remove();
      });

      // 행 번호 재정렬 + 개수 갱신
      tbody.querySelectorAll('tr').forEach((tr, i) => {
        const num = tr.querySelector('.row-num');
        if (num) num.textContent = i + 1;
      });
      countEl.textContent = `${featureMap.size}개`;

      const hs = this.highlightSelects.get(layerId);
      if (hs) hs.getFeatures().clear();
      selected.clear();
      updateSelectionUI();

      eventBus.emit(Events.FEATURE_DELETED, { layerId, count, source: 'attributeTable' });
    });

    // 머리글 탭: 정렬
    let sortColumn = null;
    let sortAsc = true;
    overlay.querySelector('thead').addEventListener('click', (e) => {
      const th = e.target.closest('th[data-column]');
      if (!th) return;
      const col = th.dataset.column;
      if (sortColumn === col) {
        sortAsc = !sortAsc;
      } else {
        sortColumn = col;
        sortAsc = true;
      }

      const rows = Array.from(tbody.querySelectorAll('tr'));
      rows.sort((a, b) => {
        const fa = featureMap.get(a.dataset.featureId);
        const fb = featureMap.get(b.dataset.featureId);
        const va = fa ? fa.get(col) : undefined;
        const vb = fb ? fb.get(col) : undefined;
        const na = parseFloat(va);
        const nb = parseFloat(vb);
        let cmp;
        if (!isNaN(na) && !isNaN(nb)) cmp = na - nb;
        else cmp = String(va ?? '').localeCompare(String(vb ?? ''), 'ko');
        return sortAsc ? cmp : -cmp;
      });
      rows.forEach((tr, i) => {
        tbody.appendChild(tr);
        const num = tr.querySelector('.row-num');
        if (num) num.textContent = i + 1;
      });
      overlay.querySelectorAll('th[data-column]').forEach(h => h.classList.remove('asc', 'desc'));
      th.classList.add(sortAsc ? 'asc' : 'desc');
    });

    // 선택 이동: 지도 이동 후 테이블만 닫기 (하이라이트는 유지해 지도에서 확인)
    zoomBtn.addEventListener('click', () => {
      if (selected.size === 0) return;
      let fullExtent = null;
      selected.forEach(fid => {
        const f = featureMap.get(fid);
        const geom = f && f.getGeometry();
        if (!geom) return;
        const ext = geom.getExtent();
        if (!fullExtent) {
          fullExtent = ext.slice();
        } else {
          fullExtent[0] = Math.min(fullExtent[0], ext[0]);
          fullExtent[1] = Math.min(fullExtent[1], ext[1]);
          fullExtent[2] = Math.max(fullExtent[2], ext[2]);
          fullExtent[3] = Math.max(fullExtent[3], ext[3]);
        }
      });
      if (fullExtent) {
        mapManager.fitExtent(fullExtent, { padding: [60, 40, 60, 40], maxZoom: 15 });
      }
      if (this.mobileSheet) {
        this.mobileSheet.el.remove();
        this.mobileSheet = null;
      }
    });

    // 닫기: 하이라이트도 제거
    overlay.querySelector('#attr-mobile-close').addEventListener('click', () => {
      this.closeMobileSheet();
    });
  }

  /**
   * 모바일 속성 테이블 닫기
   * @param {string|null} layerId - 지정 시 해당 레이어의 시트만 닫음
   */
  closeMobileSheet(layerId = null) {
    if (!this.mobileSheet) return;
    if (layerId !== null && this.mobileSheet.layerId !== layerId) return;
    this.removeHighlight(this.mobileSheet.layerId);
    this.mobileSheet.el.remove();
    this.mobileSheet = null;
  }

  /**
   * 속성 데이터를 CSV 문자열로 변환 (엑셀 한글 호환 BOM 포함)
   */
  buildCsv(features, columns) {
    const escapeCsv = (v) => {
      const s = v === undefined || v === null ? '' : String(v);
      return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [columns.map(escapeCsv).join(',')];
    features.forEach(f => {
      lines.push(columns.map(col => escapeCsv(f.get(col))).join(','));
    });
    return '\ufeff' + lines.join('\r\n');
  }

  /**
   * 속성 테이블 CSV 다운로드
   */
  downloadCsv(layerId) {
    const layerInfo = layerManager.getLayer(layerId);
    if (!layerInfo) return;

    const features = layerInfo.source.getFeatures();
    const columns = this.extractColumns(features);
    if (features.length === 0 || columns.length === 0) {
      alert('내보낼 속성 데이터가 없습니다.');
      return;
    }

    const csv = this.buildCsv(features, columns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${layerInfo.name}_속성.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /**
   * 컬럼 추출
   */
  extractColumns(features) {
    const columnSet = new Set();
    features.forEach(feature => {
      const props = feature.getProperties();
      Object.keys(props).forEach(key => {
        if (key !== 'geometry') {
          columnSet.add(key);
        }
      });
    });
    return Array.from(columnSet);
  }

  /**
   * HTML 생성
   */
  generateHTML(layerInfo, features, columns, layerId) {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

    const tableRows = features.map((feature, index) => {
      const featureId = feature.ol_uid;
      const cells = columns.map(col => {
        const value = feature.get(col);
        const displayValue = value !== undefined && value !== null ? value : '';
        return `<td title="${displayValue}">${displayValue}</td>`;
      }).join('');
      return `<tr data-feature-id="${featureId}"><td class="row-num">${index + 1}</td>${cells}</tr>`;
    }).join('');

    const headerCells = columns.map(col =>
      `<th class="sortable" data-column="${col}">${col}<span class="sort-icon"></span></th>`
    ).join('');

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>속성 테이블 - ${layerInfo.name}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }

    html, body {
      height: 100vh;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 13px;
      background: ${isDark ? '#1a1a2e' : '#ffffff'};
      color: ${isDark ? '#e4e4e7' : '#333333'};
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .header {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px;
      padding: 10px 16px;
      background: ${isDark ? '#252540' : '#f5f5f5'};
      border-bottom: 1px solid ${isDark ? '#3a3a5c' : '#e0e0e0'};
    }

    .header-title {
      font-weight: 600;
      font-size: 14px;
    }

    .header-count {
      color: ${isDark ? '#a0a0b0' : '#666666'};
      font-size: 12px;
      margin-left: 8px;
    }

    .selection-count {
      color: #4a90d9;
      font-size: 12px;
      margin-left: 8px;
    }

    .header-actions {
      display: flex;
      gap: 8px 14px;
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-end;
    }

    /* 기능별 컨트롤 묶음 (열 관리 / 열 삭제 / 선택 / 기타) */
    .action-group {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      border-radius: 6px;
      background: ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)'};
      border: 1px solid ${isDark ? '#3a3a5c' : '#e0e0e0'};
    }

    .action-group-label {
      font-size: 11px;
      color: ${isDark ? '#9a9ab0' : '#888'};
      margin-right: 2px;
      white-space: nowrap;
    }

    .btn {
      padding: 6px 12px;
      border: none;
      border-radius: 4px;
      font-size: 12px;
      cursor: pointer;
      background: ${isDark ? '#3a3a5c' : '#e0e0e0'};
      color: ${isDark ? '#e4e4e7' : '#333333'};
      transition: background 0.2s;
    }

    .btn:hover {
      background: ${isDark ? '#4a4a6c' : '#d0d0d0'};
    }

    .btn-primary {
      background: #4a90d9;
      color: white;
    }

    .btn-primary:hover {
      background: #3a80c9;
    }

    .btn-danger {
      background: #dc3545;
      color: white;
    }

    .btn-danger:hover {
      background: #c82333;
    }

    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .col-name-input {
      padding: 6px 8px;
      border: 1px solid ${isDark ? '#4a4a6c' : '#ccc'};
      border-radius: 4px;
      font-size: 12px;
      width: 110px;
      background: ${isDark ? '#1a1a2e' : '#ffffff'};
      color: ${isDark ? '#e4e4e7' : '#333333'};
      outline: none;
    }

    .col-name-input:focus {
      border-color: #4a90d9;
    }

    .table-container {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }

    thead {
      position: sticky;
      top: 0;
      z-index: 10;
    }

    th {
      background: ${isDark ? '#2a2a45' : '#f0f0f0'};
      padding: 8px 10px;
      text-align: left;
      font-weight: 600;
      border-bottom: 2px solid ${isDark ? '#4a4a6c' : '#ccc'};
      white-space: nowrap;
      cursor: pointer;
      user-select: none;
    }

    th:hover {
      background: ${isDark ? '#353555' : '#e5e5e5'};
    }

    th.sortable .sort-icon::after {
      content: '⇅';
      margin-left: 4px;
      opacity: 0.4;
    }

    th.asc .sort-icon::after {
      content: '↑';
      opacity: 1;
    }

    th.desc .sort-icon::after {
      content: '↓';
      opacity: 1;
    }

    td {
      padding: 6px 10px;
      border-bottom: 1px solid ${isDark ? '#3a3a5c' : '#eee'};
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    td.editable:hover {
      background: ${isDark ? '#3a3a5c' : '#f0f0f0'};
      cursor: text;
    }

    td.editing {
      padding: 2px;
    }

    td.editing input {
      width: 100%;
      padding: 4px 8px;
      border: 2px solid #4a90d9;
      border-radius: 3px;
      font-size: 12px;
      background: ${isDark ? '#1a1a2e' : '#ffffff'};
      color: ${isDark ? '#e4e4e7' : '#333333'};
      outline: none;
    }

    .row-num {
      width: 50px;
      text-align: center;
      color: ${isDark ? '#808090' : '#999'};
      background: ${isDark ? '#222238' : '#fafafa'};
    }

    tr:hover {
      background: ${isDark ? '#2a2a45' : '#f5f5f5'};
    }

    tr.selected {
      background: ${isDark ? '#3a4a5c' : '#e3f2fd'} !important;
    }

    tr.selected td {
      border-color: ${isDark ? '#4a5a6c' : '#bbdefb'};
    }

    .empty-message {
      padding: 40px;
      text-align: center;
      color: ${isDark ? '#808090' : '#999'};
    }

    .help-text {
      flex: 0 0 auto;
      font-size: 11px;
      color: ${isDark ? '#808090' : '#999'};
      padding: 8px 16px;
      background: ${isDark ? '#1a1a2e' : '#fafafa'};
      border-bottom: 1px solid ${isDark ? '#3a3a5c' : '#e0e0e0'};
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <span class="header-title">${layerInfo.name}</span>
      <span class="header-count">(${features.length}개 피처)</span>
      <span class="selection-count" id="selection-count"></span>
    </div>
    <div class="header-actions">
      <div class="action-group">
        <span class="action-group-label">열 추가</span>
        <input type="text" id="new-column-name" class="col-name-input" placeholder="새 열 이름">
        <button class="btn" id="btn-add-column" title="속성 테이블에 새 열 추가">추가</button>
      </div>
      <div class="action-group">
        <span class="action-group-label">열 삭제</span>
        <select id="del-column-select" class="col-name-input" title="삭제할 열 선택"></select>
        <button class="btn btn-danger" id="btn-del-column" title="선택한 열 삭제">삭제</button>
      </div>
      <div class="action-group">
        <span class="action-group-label">선택 피처</span>
        <button class="btn" id="btn-zoom-selected" title="선택한 피처로 이동">이동</button>
        <button class="btn btn-danger" id="btn-delete-selected" title="선택한 피처 삭제" disabled>삭제</button>
      </div>
      <div class="action-group">
        <button class="btn" id="btn-export-csv" title="속성 테이블을 CSV 파일로 저장">CSV 다운로드</button>
      </div>
    </div>
  </div>
  <div class="help-text">Ctrl+클릭: 다중 선택 | Shift+클릭: 범위 선택 | 더블클릭: 셀 편집 | Delete: 선택 삭제</div>
  <div class="table-container">
    ${features.length > 0 ? `
    <table>
      <thead>
        <tr>
          <th class="row-num">#</th>
          ${headerCells}
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>
    ` : '<div class="empty-message">피처가 없습니다.</div>'}
  </div>
</body>
</html>
    `;
  }

  /**
   * 창 내부 이벤트 바인딩
   */
  bindWindowEvents(win, layerId, features, columns) {
    const self = this;

    // 피처 ID를 문자열로 저장하는 맵 생성
    const featureMap = new Map();
    features.forEach(f => {
      featureMap.set(String(f.ol_uid), f);
    });

    // document.write 후 즉시 이벤트 바인딩 (setTimeout으로 DOM 렌더링 대기)
    setTimeout(function() {
      const doc = win.document;
      if (!doc || !doc.body) return;

      let selectedFeatureIds = new Set();
      let lastSelectedIndex = -1;
      let sortColumn = null;
      let sortAsc = true;
      let editingCell = null;

      const btnDelete = doc.getElementById('btn-delete-selected');
      const selectionCountEl = doc.getElementById('selection-count');

      // 행 클릭 - 선택
      doc.querySelectorAll('tbody tr').forEach((tr, index) => {
        tr.addEventListener('click', function(e) {
          const featureId = this.dataset.featureId;
          const rowIndex = getRowIndex(this);

          if (e.ctrlKey || e.metaKey) {
            // Ctrl+클릭: 토글 선택
            toggleSelection(featureId);
            lastSelectedIndex = rowIndex;
          } else if (e.shiftKey && lastSelectedIndex >= 0) {
            // Shift+클릭: 범위 선택
            rangeSelect(lastSelectedIndex, rowIndex);
          } else {
            // 일반 클릭: 단일 선택
            clearSelection();
            addSelection(featureId);
            lastSelectedIndex = rowIndex;
          }

          updateSelectionUI();
          updateMapHighlight();
        });
      });

      // 셀 더블클릭 - 편집
      doc.querySelectorAll('tbody td:not(.row-num)').forEach((td, cellIndex) => {
        td.classList.add('editable');
        td.addEventListener('dblclick', function(e) {
          e.stopPropagation();
          startEditing(this);
        });
      });

      // 헤더 클릭 - 정렬
      doc.querySelectorAll('th.sortable').forEach(th => {
        th.addEventListener('click', function() {
          const col = this.dataset.column;
          if (sortColumn === col) {
            sortAsc = !sortAsc;
          } else {
            sortColumn = col;
            sortAsc = true;
          }
          sortTable(col, sortAsc);
          updateSortUI(col, sortAsc);
        });
      });

      // 선택 피처로 이동 버튼
      const btnZoom = doc.getElementById('btn-zoom-selected');
      if (btnZoom) {
        btnZoom.addEventListener('click', zoomToSelected);
      }

      // 선택 삭제 버튼
      if (btnDelete) {
        btnDelete.addEventListener('click', deleteSelected);
      }

      // CSV 다운로드 버튼
      const btnExportCsv = doc.getElementById('btn-export-csv');
      if (btnExportCsv) {
        btnExportCsv.addEventListener('click', function() {
          self.downloadCsv(layerId);
        });
      }

      // 열 추가 버튼
      const btnAddColumn = doc.getElementById('btn-add-column');
      const newColInput = doc.getElementById('new-column-name');

      function addColumn() {
        const name = (newColInput ? newColInput.value : '').trim();
        if (!name) {
          win.alert('추가할 열 이름을 입력하세요.');
          if (newColInput) newColInput.focus();
          return;
        }
        if (columns.includes(name) || name === 'geometry') {
          win.alert('이미 존재하는 열 이름입니다.');
          return;
        }
        if (featureMap.size === 0) {
          win.alert('피처가 없어 열을 추가할 수 없습니다.');
          return;
        }

        // 모든 피처에 빈 값 속성 추가 (CSV/프로젝트 저장에 반영됨)
        featureMap.forEach(f => {
          if (f.get(name) === undefined) f.set(name, '');
        });
        columns.push(name);

        // 헤더에 정렬 가능한 열 추가
        const headRow = doc.querySelector('thead tr');
        if (headRow) {
          const th = doc.createElement('th');
          th.className = 'sortable';
          th.dataset.column = name;
          th.textContent = name;
          const span = doc.createElement('span');
          span.className = 'sort-icon';
          th.appendChild(span);
          th.addEventListener('click', function() {
            const col = this.dataset.column;
            if (sortColumn === col) {
              sortAsc = !sortAsc;
            } else {
              sortColumn = col;
              sortAsc = true;
            }
            sortTable(col, sortAsc);
            updateSortUI(col, sortAsc);
          });
          headRow.appendChild(th);
        }

        // 각 행에 편집 가능한 빈 셀 추가 (더블클릭으로 텍스트 입력)
        doc.querySelectorAll('tbody tr').forEach(tr => {
          const td = doc.createElement('td');
          td.className = 'editable';
          td.title = '';
          td.textContent = '';
          td.addEventListener('dblclick', function(e) {
            e.stopPropagation();
            startEditing(this);
          });
          tr.appendChild(td);
        });

        if (newColInput) newColInput.value = '';
        refreshDelColumnOptions();
      }

      if (btnAddColumn) {
        btnAddColumn.addEventListener('click', addColumn);
      }
      if (newColInput) {
        newColInput.addEventListener('keydown', function(e) {
          if (e.key === 'Enter') addColumn();
          e.stopPropagation();
        });
      }

      // 열 삭제 버튼
      const btnDelColumn = doc.getElementById('btn-del-column');

      function refreshDelColumnOptions() {
        const sel = doc.getElementById('del-column-select');
        if (!sel) return;
        sel.innerHTML = '';
        if (columns.length === 0) {
          const opt = doc.createElement('option');
          opt.value = '';
          opt.textContent = '삭제할 열 없음';
          sel.appendChild(opt);
          return;
        }
        columns.forEach(col => {
          const opt = doc.createElement('option');
          opt.value = col;
          opt.textContent = col;
          sel.appendChild(opt);
        });
      }

      function removeColumn(name) {
        if (!name) {
          win.alert('삭제할 열을 선택하세요.');
          return;
        }
        const colIndex = columns.indexOf(name);
        if (colIndex === -1) return;
        if (!win.confirm(`'${name}' 열을 삭제하시겠습니까? 이 열의 모든 값이 사라집니다.`)) return;

        // 편집 중이면 취소
        if (editingCell) cancelEditing();

        // 모든 피처에서 속성 제거 (CSV/프로젝트 저장에 반영됨)
        featureMap.forEach(f => f.unset(name));

        // 헤더에서 해당 열(th) 제거
        doc.querySelectorAll('thead th[data-column]').forEach(th => {
          if (th.dataset.column === name) th.remove();
        });

        // 각 행에서 해당 셀 제거 (row-num 다음이 columns[0])
        doc.querySelectorAll('tbody tr').forEach(tr => {
          const cell = tr.querySelectorAll('td')[colIndex + 1];
          if (cell) cell.remove();
        });

        // columns 배열에서 제거 (DOM 셀과 순서 동기화 유지)
        columns.splice(colIndex, 1);
        if (sortColumn === name) sortColumn = null;

        refreshDelColumnOptions();
      }

      if (btnDelColumn) {
        btnDelColumn.addEventListener('click', function() {
          const sel = doc.getElementById('del-column-select');
          removeColumn(sel ? sel.value : '');
        });
      }

      // 삭제 목록 초기화
      refreshDelColumnOptions();

      // Delete 키 단축키
      doc.addEventListener('keydown', function(e) {
        if (e.key === 'Delete' && selectedFeatureIds.size > 0 && !editingCell) {
          deleteSelected();
        }
        // Escape: 편집 취소
        if (e.key === 'Escape' && editingCell) {
          cancelEditing();
        }
      });

      function getRowIndex(tr) {
        const rows = Array.from(doc.querySelectorAll('tbody tr'));
        return rows.indexOf(tr);
      }

      function getAllRows() {
        return Array.from(doc.querySelectorAll('tbody tr'));
      }

      function toggleSelection(featureId) {
        if (selectedFeatureIds.has(featureId)) {
          selectedFeatureIds.delete(featureId);
        } else {
          selectedFeatureIds.add(featureId);
        }
      }

      function addSelection(featureId) {
        selectedFeatureIds.add(featureId);
      }

      function clearSelection() {
        selectedFeatureIds.clear();
      }

      function rangeSelect(startIndex, endIndex) {
        const rows = getAllRows();
        const minIndex = Math.min(startIndex, endIndex);
        const maxIndex = Math.max(startIndex, endIndex);

        for (let i = minIndex; i <= maxIndex; i++) {
          const row = rows[i];
          if (row) {
            selectedFeatureIds.add(row.dataset.featureId);
          }
        }
      }

      function updateSelectionUI() {
        doc.querySelectorAll('tbody tr').forEach(tr => {
          if (selectedFeatureIds.has(tr.dataset.featureId)) {
            tr.classList.add('selected');
          } else {
            tr.classList.remove('selected');
          }
        });

        // 선택 개수 표시
        if (selectionCountEl) {
          if (selectedFeatureIds.size > 0) {
            selectionCountEl.textContent = `(${selectedFeatureIds.size}개 선택됨)`;
          } else {
            selectionCountEl.textContent = '';
          }
        }

        // 삭제 버튼 활성화/비활성화
        if (btnDelete) {
          btnDelete.disabled = selectedFeatureIds.size === 0;
        }
      }

      function updateMapHighlight() {
        const highlightSelect = self.highlightSelects.get(layerId);
        if (highlightSelect) {
          highlightSelect.getFeatures().clear();
          selectedFeatureIds.forEach(fid => {
            const feature = featureMap.get(fid);
            if (feature) {
              highlightSelect.getFeatures().push(feature);
            }
          });
        }

        // 단일 선택 시 이벤트 발생
        if (selectedFeatureIds.size === 1) {
          const fid = selectedFeatureIds.values().next().value;
          const feature = featureMap.get(fid);
          if (feature) {
            eventBus.emit(Events.FEATURE_SELECTED, { feature });
          }
        }
      }

      function zoomToSelected() {
        if (selectedFeatureIds.size === 0) {
          win.alert('먼저 피처를 선택하세요.');
          return;
        }

        // 선택된 모든 피처의 extent 계산
        let fullExtent = null;
        selectedFeatureIds.forEach(fid => {
          const feature = featureMap.get(fid);
          if (feature) {
            const geometry = feature.getGeometry();
            if (geometry) {
              const extent = geometry.getExtent();
              if (!fullExtent) {
                fullExtent = extent.slice();
              } else {
                fullExtent[0] = Math.min(fullExtent[0], extent[0]);
                fullExtent[1] = Math.min(fullExtent[1], extent[1]);
                fullExtent[2] = Math.max(fullExtent[2], extent[2]);
                fullExtent[3] = Math.max(fullExtent[3], extent[3]);
              }
            }
          }
        });

        if (fullExtent) {
          mapManager.fitExtent(fullExtent, {
            padding: [100, 100, 100, 100],
            maxZoom: 15
          });
        }
      }

      function deleteSelected() {
        if (selectedFeatureIds.size === 0) return;

        const count = selectedFeatureIds.size;
        const message = count === 1
          ? '선택한 피처를 삭제하시겠습니까?'
          : `선택한 ${count}개의 피처를 삭제하시겠습니까?`;

        if (!win.confirm(message)) return;

        const layerInfo = layerManager.getLayer(layerId);
        if (!layerInfo || !layerInfo.source) return;

        // 피처 삭제
        selectedFeatureIds.forEach(fid => {
          const feature = featureMap.get(fid);
          if (feature) {
            layerInfo.source.removeFeature(feature);
            featureMap.delete(fid);
          }
        });

        // 테이블에서 행 제거
        selectedFeatureIds.forEach(fid => {
          const row = doc.querySelector(`tbody tr[data-feature-id="${fid}"]`);
          if (row) row.remove();
        });

        // 행 번호 재정렬
        doc.querySelectorAll('tbody tr').forEach((tr, index) => {
          const rowNum = tr.querySelector('.row-num');
          if (rowNum) rowNum.textContent = index + 1;
        });

        // 피처 개수 업데이트
        const countEl = doc.querySelector('.header-count');
        if (countEl) {
          const newCount = featureMap.size;
          countEl.textContent = `(${newCount}개 피처)`;
        }

        // 하이라이트 클리어
        const highlightSelect = self.highlightSelects.get(layerId);
        if (highlightSelect) {
          highlightSelect.getFeatures().clear();
        }

        clearSelection();
        updateSelectionUI();

        // 이 창은 이미 행을 제거했으므로 자기 자신은 재갱신하지 않도록 출처 표시
        eventBus.emit(Events.FEATURE_DELETED, { layerId, count, source: 'attributeTable' });
      }

      function startEditing(td) {
        if (editingCell) {
          saveEditing();
        }

        const tr = td.closest('tr');
        const featureId = tr.dataset.featureId;
        const feature = featureMap.get(featureId);
        if (!feature) return;

        // 컬럼 인덱스 찾기
        const cells = Array.from(tr.querySelectorAll('td'));
        const cellIndex = cells.indexOf(td) - 1; // row-num 셀 제외
        if (cellIndex < 0 || cellIndex >= columns.length) return;

        const column = columns[cellIndex];
        const currentValue = feature.get(column);

        editingCell = {
          td: td,
          featureId: featureId,
          column: column,
          originalValue: currentValue
        };

        td.classList.add('editing');
        const input = doc.createElement('input');
        input.type = 'text';
        input.value = currentValue !== undefined && currentValue !== null ? currentValue : '';
        td.innerHTML = '';
        td.appendChild(input);
        input.focus();
        input.select();

        input.addEventListener('blur', function() {
          saveEditing();
        });

        input.addEventListener('keydown', function(e) {
          if (e.key === 'Enter') {
            saveEditing();
          } else if (e.key === 'Escape') {
            cancelEditing();
          }
          e.stopPropagation();
        });
      }

      function saveEditing() {
        if (!editingCell) return;

        const { td, featureId, column, originalValue } = editingCell;
        const input = td.querySelector('input');
        const newValue = input ? input.value : originalValue;

        const feature = featureMap.get(featureId);
        if (feature && newValue !== originalValue) {
          // 숫자 변환 시도
          const numValue = parseFloat(newValue);
          const finalValue = !isNaN(numValue) && newValue.trim() !== '' ? numValue : newValue;
          feature.set(column, finalValue);
        }

        td.classList.remove('editing');
        td.innerHTML = '';
        td.textContent = newValue;
        td.title = newValue;

        editingCell = null;
      }

      function cancelEditing() {
        if (!editingCell) return;

        const { td, originalValue } = editingCell;
        td.classList.remove('editing');
        td.innerHTML = '';
        td.textContent = originalValue !== undefined && originalValue !== null ? originalValue : '';
        td.title = td.textContent;

        editingCell = null;
      }

      function sortTable(column, ascending) {
        const tbody = doc.querySelector('tbody');
        if (!tbody) return;

        const rows = Array.from(tbody.querySelectorAll('tr'));

        rows.sort((a, b) => {
          const aFeatureId = a.dataset.featureId;
          const bFeatureId = b.dataset.featureId;
          const aFeature = featureMap.get(aFeatureId);
          const bFeature = featureMap.get(bFeatureId);

          const valA = aFeature ? aFeature.get(column) : '';
          const valB = bFeature ? bFeature.get(column) : '';

          // 숫자 비교
          const numA = parseFloat(valA);
          const numB = parseFloat(valB);
          if (!isNaN(numA) && !isNaN(numB)) {
            return ascending ? numA - numB : numB - numA;
          }

          // 문자열 비교
          const strA = String(valA || '').toLowerCase();
          const strB = String(valB || '').toLowerCase();
          return ascending ? strA.localeCompare(strB) : strB.localeCompare(strA);
        });

        // 행 번호 재정렬 및 DOM 업데이트
        rows.forEach((row, index) => {
          const rowNum = row.querySelector('.row-num');
          if (rowNum) {
            rowNum.textContent = index + 1;
          }
          tbody.appendChild(row);
        });
      }

      function updateSortUI(column, ascending) {
        doc.querySelectorAll('th.sortable').forEach(th => {
          th.classList.remove('asc', 'desc');
          if (th.dataset.column === column) {
            th.classList.add(ascending ? 'asc' : 'desc');
          }
        });
      }
    }, 100);
  }

  /**
   * 창 새로고침
   * 이미 열린 창은 닫지 않고 내용만 다시 그린다 (창 크기/위치 유지 → 전체화면 튀는 문제 방지).
   * 창이 없을 때만 새로 연다.
   */
  refreshWindow(layerId) {
    const win = this.openWindows.get(layerId);

    if (win && !win.closed) {
      const layerInfo = layerManager.getLayer(layerId);
      if (!layerInfo) {
        this.closeWindow(layerId);
        return;
      }

      const features = layerInfo.source.getFeatures();
      const columns = this.extractColumns(features);
      const html = this.generateHTML(layerInfo, features, columns, layerId);

      // 기존 창 문서를 그대로 다시 그림 (새 window.open 없음)
      win.document.open();
      win.document.write(html);
      win.document.close();

      // 하이라이트 재설정 + 창 내부 이벤트 재바인딩
      this.setupHighlight(layerId, layerInfo);
      this.bindWindowEvents(win, layerId, features, columns);
      return;
    }

    // 열린 창이 없으면 새로 연다
    this.closeWindow(layerId);
    this.open(layerId);
  }

  /**
   * 모든 창 새로고침
   */
  refreshAllWindows() {
    // 키를 배열로 스냅샷: refreshWindow가 openWindows를 delete/set 하므로
    // 살아있는 이터레이터를 직접 돌면 같은 키를 무한 재방문(창 폭주)하게 됨
    for (const layerId of [...this.openWindows.keys()]) {
      this.refreshWindow(layerId);
    }
  }

  /**
   * 창 닫기
   */
  closeWindow(layerId) {
    const win = this.openWindows.get(layerId);
    if (win && !win.closed) {
      win.close();
    }
    this.openWindows.delete(layerId);
    this.removeHighlight(layerId);
  }

  /**
   * 하이라이트 인터랙션 설정
   */
  setupHighlight(layerId, layerInfo) {
    this.removeHighlight(layerId);

    const map = mapManager.getMap();

    const highlightSelect = new Select({
      condition: () => false,
      style: HIGHLIGHT_STYLE,
      layers: [layerInfo.olLayer]
    });

    map.addInteraction(highlightSelect);
    this.highlightSelects.set(layerId, highlightSelect);
  }

  /**
   * 하이라이트 제거
   */
  removeHighlight(layerId) {
    const highlightSelect = this.highlightSelects.get(layerId);
    if (highlightSelect) {
      const map = mapManager.getMap();
      if (map) {
        map.removeInteraction(highlightSelect);
      }
      this.highlightSelects.delete(layerId);
    }
  }
}

// 싱글톤 인스턴스
export const attributeTable = new AttributeTable();
